import {
  DefinedInitialDataOptions,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { QueryConfig } from '@/lib/api-clients/react-query';
import { privateV2Api } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { OrderStatus, OrderType } from '@/types/enums';
import { useShallow } from 'zustand/react/shallow';
import { getByNormalizedKey } from '@/utils/helpers';

interface GetOrdersParams {
  id?: string | string[];
  symbol?: string;
  orderType?: OrderType;
  status?: OrderStatus;
  startTime?: number;
  endTime?: number;
  limit?: number;
}

type NewResponse = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: string;
  tpPrice?: string;
  slPrice?: string;
  size: string;
  reduceOnly: boolean;
  status: OrderStatus.NEW;
  initMarginRatio: string;
  postTime: number;
};

type FilledResponse = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: string;
  tpPrice?: string;
  slPrice?: string;
  size: string;
  reduceOnly: boolean;
  status: OrderStatus.FILLED;
  initMarginRatio: string;
  lastFilledSize: string;
  lastFilledPrice: string;
  lastFilledTime: number;
  avgFilledPrice: string;
  realizedPnl: string;
  settledFunding: string;
  fees: string;
  postTime: number;
  markPrice: string;
};

type CancelledResponse = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: string;
  tpPrice?: string;
  slPrice?: string;
  size: string;
  reduceOnly: boolean;
  status: OrderStatus.CANCELLED;
  initMarginRatio: string;
  postTime: number;
};

type RejectedResponse = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: string;
  tpPrice?: string;
  slPrice?: string;
  size: string;
  reduceOnly: boolean;
  status: OrderStatus.REJECTED;
  initMarginRatio: string;
  postTime: number;
  code: number;
};

type PartiallyFilledResponse = Omit<FilledResponse, 'status'> & {
  status: OrderStatus.PARTIALLY_FILLED;
  parentId: string;
};

export type OrderResponse =
  | NewResponse
  | FilledResponse
  | PartiallyFilledResponse
  | CancelledResponse
  | RejectedResponse;

export const getOrders = async (
  { id, symbol, orderType, status, startTime, endTime, limit }: GetOrdersParams,
  apiKey?: string,
): Promise<OrderResponse[]> => {
  return privateV2Api.get<OrderResponse[]>('/v2/orders', {
    params: {
      id: typeof id === 'string' ? id : id?.join(','),
      symbol,
      orderType,
      status,
      startTime,
      endTime,
      limit,
    },
    ...(apiKey
      ? {
          headers: {
            'X-API-KEY': apiKey,
          },
        }
      : {}),
  });
};

const getOrdersQueryKey = (params: GetOrdersParams, apiKey?: string) => {
  const {
    endTime = useAccountStore.getState().getAccurateTime(),
    limit = 1000,
    ...restOfParams
  } = params;
  return ['Orders', apiKey, restOfParams];
};

type UseOrdersOptions = GetOrdersParams & {
  queryConfig?: Partial<DefinedInitialDataOptions<OrderResponse[]>>;
  onSuccess?: (res: OrderResponse[]) => void;
};

export const useOrders = ({
  queryConfig,
  onSuccess,
  ...params
}: UseOrdersOptions = {}) => {
  const { hasHydrated, hasAuth, apiKey } = useAccountStore(
    useShallow((state) => {
      const account = getByNormalizedKey(state.accounts, state.address);
      return {
        hasHydrated: !!account && account.hasHydrated,
        hasAuth: state.hasAuth,
        apiKey: state.getAuthParams()?.apiKey,
      };
    }),
  );

  const result = useQuery<OrderResponse[]>({
    ...(queryConfig ?? {}),
    queryKey: getOrdersQueryKey(params, apiKey),
    queryFn: () =>
      getOrders(params, apiKey).then((res) => {
        onSuccess?.(res);
        return res;
      }),
    enabled:
      queryConfig?.enabled !== false && hasAuth && hasHydrated && !!apiKey,
  });

  return result;
};

export function useOpenOrders() {
  return useOrders({
    status: OrderStatus.NEW,
    queryConfig: { staleTime: 0 },
  });
}
